import { IProvider } from '@/services/provider/provider.type';
import {
  COMMON_STEPPER_CONTENT,
  STATUS_CODES,
} from '../global/global.constant';

export const LIMIT = { limit: 100, page: 1 };
export const ALERTS_PROPS = {
  ALERTS: {
    SELECT_TYPE: 'Select Type',
    TYPE: 'Type',
    SELECT_LEVELS: 'Select Level',
    LEVELS: 'Level',
    SELECT_STATUS: 'Select Status',
    STATUS: 'Select Status',
    SEARCH_PLACEHOLDER: 'Alert Id (UUID)',
    STATUS_FILTERS: {
      ALL: { value: '1', label: 'All' },
      OPEN: { value: 'OPEN', label: 'Open' },
      CLOSED: { value: 'CLOSED', label: 'Closed' },
    },
  },
  COLUMNS: {
    ALERT_ID: 'Alert Id',
    OPERATOR_ID: 'Operator Id',
    MARKET_ID: 'Market Id',
    DEVICE_ID: 'Device Id',
    TYPE: 'Type',
    STATUS_CODE: 'Status Code',
    LEVEL: 'Level',
    STATUS: 'Status',
    TRIGGERED_ON: 'Triggered On',
    CLOSED_ON: 'Closed On',
  },
};
export const EAlertLevels = [
  {
    id: '1',
    name: 'All Levels',
  },
  {
    id: 'WARNING',
    name: 'WARNING',
  },
  {
    id: 'MINOR',
    name: 'MINOR',
  },
  {
    id: 'MAJOR',
    name: 'MAJOR',
  },
  {
    id: 'CRITICAL',
    name: 'CRITICAL',
  },
  {
    id: 'FATAL',
    name: 'FATAL',
  },
];

export const EAlertStatus = [
  {
    id: '1',
    name: 'All Status',
  },
  {
    id: 'OPEN',
    name: 'OPEN',
  },
  {
    id: 'CLOSED',
    name: 'CLOSED',
  },
];

export const EAlertType = [
  {
    id: '1',
    name: 'All Types',
  },
  {
    id: 'PLANOGRAM_REJECTION',
    name: 'Planogram Rejection',
  },
  {
    id: 'CAMERA_OBSTRUCTION',
    name: 'Camera Obstruction',
  },
  {
    id: 'CAMERA_BLOCKED',
    name: 'Camera Blocked',
  },
  {
    id: 'CAMERA_DISCONNECTED',
    name: 'Camera Disconnected',
  },
  {
    id: 'CCR_DISCONNECTED',
    name: 'CCR Disconnected',
  },
  {
    id: 'TEMPERATURE',
    name: 'Temperature',
  },
  {
    id: 'INTERNET_CONNECTIVITY',
    name: 'Internet Connectivity',
  },
  {
    id: 'LOCK_ERROR',
    name: 'Lock Error',
  },
  {
    id: 'DOOR_ERROR',
    name: 'Door Error',
  },
  {
    id: 'BATTERY_LOW',
    name: 'Battery Low',
  },
  {
    id: 'PAYMENT_FAILUER',
    name: 'Payment Failure',
  },
  {
    id: 'WEAK_CONNECTIVITY',
    name: 'Weak Connectivity',
  },
  {
    id: 'OFFLINE',
    name: 'Offline',
  },
  {
    id: 'EMPTY_PLANOGRAM',
    name: 'Empty Planogram',
  },
  {
    id: 'OPEN_TIMEOUT',
    name: 'Open Timeout',
  },
];

export const ALERT_MESSAGES = {
  CREATION: 'Alert Created',
};

// Define the types for the input and output data structures.
interface AlertLogEntry {
  action: string;
  server: string;
  statusCode: number;
  activityDtm: string;
  message?: string;
}

interface Logs {
  alertLog?: AlertLogEntry[];
}

export type AlertStepActionStatus = Record<
  string,
  boolean | 'Not Started' | 'Failed' | 'Success'
>;

interface ServerStatus {
  actionStatus: AlertStepActionStatus;
  actionMessages: Record<string, string | null>; // Added this line
  message?: string | null;
  latestEventDtm?: string;
  statusCode?: number;
  success?: boolean;
  activityDtm?: string;
  partialSuccess?: boolean;
}

type CustomisedStepperResponse = Record<string, ServerStatus> & {
  eachStepSummary: Record<string, AlertStepActionStatus>;
};
export const COMMON_STEPPER_BG_COLORS = {
  DEFAULT: '#FCD58D',
  IN_PROGRESS: '#6366f1',
  SUCCESS: '#FF7F30',
  PARTIAL_SUCCESS: '#FCBB8D',
  CREATED: '#FF7F30',
  FAILURE: '#E75065',
  CLOSED: '#3F3F46',
  NOT_STARTED: '#6366F1',
};

export const ALERT_UI_CONFIG = {
  STATUS_TYPES: {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
  },
  STATUS_COLOR_MAP: {
    OPEN: COMMON_STEPPER_BG_COLORS.SUCCESS,
    CLOSED: COMMON_STEPPER_BG_COLORS.CLOSED,
    DEFAULT: COMMON_STEPPER_BG_COLORS.DEFAULT,
  },
} as const;

// Success and Failure messages for display in tooltips — centralized so components can reuse
const { STEPPER_TITLES, ACTIONS } = COMMON_STEPPER_CONTENT;
export const ALERT_STEP_SUCCESS_MESSAGES = {
  [STEPPER_TITLES.GATEWAY]: {
    [ACTIONS.GATEWAY_STEP_ACTIONS.CREATE]: 'Alert Reached to Gateway',
    [ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE]: 'Alert Closed at Gateway',
  },
  [STEPPER_TITLES.VMS]: {
    [ACTIONS.VMS_STEP_ACTIONS.CREATE_SYNC]: 'Alert Created at VMS',
    [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: 'Alert Processed at VMS',
    [ACTIONS.VMS_STEP_ACTIONS.UPDATE_SYNC]: 'Alert Closed at VMS',
  },
  [STEPPER_TITLES.CLIENTS]: {
    [ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC]: 'Alert Created at Client',
    [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: 'Alert Processed at Client',
    [ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC]: 'Alert Closed at Client',
  },
} as const;

export const ALERT_STEP_FAILURE_MESSAGES = {
  [STEPPER_TITLES.GATEWAY]: {
    [ACTIONS.GATEWAY_STEP_ACTIONS.CREATE]: 'Alert Failed to Reach Gateway',
    [ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE]: 'Alert Failed to Close at Gateway',
  },
  [STEPPER_TITLES.VMS]: {
    [ACTIONS.VMS_STEP_ACTIONS.CREATE_SYNC]: 'Alert Failed to Create at VMS',
    [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: 'Alert Failed to Process at VMS',
    [ACTIONS.VMS_STEP_ACTIONS.UPDATE_SYNC]: 'Alert Failed to Close at VMS',
  },
  [STEPPER_TITLES.CLIENTS]: {
    [ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC]:
      'Alert Failed to Create at Client',
    [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: 'Alert Failed to Process at Client',
    [ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC]:
      'Alert Failed to Close at Client',
  },
} as const;

// NEW CODE CHANGES BASED ON PROVIDER CONFIG FOR ALERT STEPPER FLOW

export const providerAlertStepperConfig = (
  providersData: IProvider[],
  partnerIdFromAlertsResponse: string,
) => {
  // Getting Alerts Config Object from Providers Data Config Object
  const alertsConfigFromProvider = providersData?.find(
    (provider) => provider?.partnerId === partnerIdFromAlertsResponse,
  )?.configs?.VDI_SETTINGS?.ALERTS;

  if (!alertsConfigFromProvider) {
    // returning Empty Array as Alert Stepper Flow Steps if Alerts config is not found in Provider Config
    return {
      alertsConfigFromProvider: null,
      ENABLED_ALERT_STEPS: [],
    };
  }
  // 🔥 Fixed step order
  const ALERTS_STEPPER_ORDER = ['MMS', 'GATEWAY', 'VMS', 'CLIENTS'];

  const ENABLED_ALERT_STEPS: { stepTitle: string; label: any }[] = [];
  const EXCLUDED_ALERT_STEPS: { stepTitle: string; label: any }[] = [];

  ALERTS_STEPPER_ORDER.forEach((key) => {
    const value = alertsConfigFromProvider[key];

    if (value?.ENABLED) {
      ENABLED_ALERT_STEPS.push({
        stepTitle: key,
        label: value?.AUTH?.AUTH_TYPE?.toUpperCase(),
      });
    } else {
      EXCLUDED_ALERT_STEPS.push({
        stepTitle: key,
        label: value?.AUTH?.AUTH_TYPE?.toUpperCase(),
      });
    }
  });

  return {
    alertsConfigFromProvider,
    ENABLED_ALERT_STEPS,
    EXCLUDED_ALERT_STEPS,
    ALERTS_STEPPER_ORDER,
  };
};

export function parseAlertStepperData(
  allowedAlertFlowSteps: { stepTitle: string; label: string }[],
  logs: Logs,
): CustomisedStepperResponse {
  const stepperProgress: Record<string, ServerStatus> = {
    [STEPPER_TITLES.GATEWAY]: {
      actionStatus: {
        [ACTIONS.GATEWAY_STEP_ACTIONS.CREATE]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE]: ACTIONS.STATUS.NOT_STARTED,
      },
      actionMessages: {},
    },
    [STEPPER_TITLES.VMS]: {
      actionStatus: {
        [ACTIONS.VMS_STEP_ACTIONS.CREATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.VMS_STEP_ACTIONS.UPDATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.VMS_STEP_ACTIONS.GET_SYNC]: ACTIONS.STATUS.NOT_STARTED,
      },
      actionMessages: {},
    },
    [STEPPER_TITLES.CLIENTS]: {
      actionStatus: {
        [ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
      },
      actionMessages: {},
    },
  };

  try {
    // Safely access the alertLog array, defaulting to an empty array
    // if `logs` or `logs.alertLog` are not present or not an array.
    const alertLogs: AlertLogEntry[] = Array.isArray(logs?.alertLog)
      ? logs.alertLog
      : [];

    const sortedLogs: AlertLogEntry[] = [...alertLogs].sort((a, b) => {
      const dateA = new Date(a?.activityDtm)?.getTime();
      const dateB = new Date(b?.activityDtm)?.getTime();
      // Handle invalid dates by returning 0, which keeps their relative order.
      return (isNaN(dateA) ? 0 : dateA) - (isNaN(dateB) ? 0 : dateB);
    });

    for (const alert of sortedLogs) {
      const { action, server, statusCode, activityDtm, message } = alert || {};
      const serverValueOfAlertAction = server?.toUpperCase();
      const stepLabelWithMatchingServer = allowedAlertFlowSteps?.find(
        (step: any) => step?.label?.toUpperCase() === serverValueOfAlertAction,
      );
      const serverKey = stepLabelWithMatchingServer?.stepTitle;

      // Ensure serverKey is a defined string before using it to index the object.
      if (typeof serverKey !== 'string' || !stepperProgress[serverKey]) {
        continue;
      }

      const serverObject = stepperProgress[serverKey];

      // Update the message specific to this action
      // We store it in actionMessages under the specific action name
      if (action) {
        serverObject.actionMessages[action] =
          message ||
          (statusCode > STATUS_CODES.CREATED
            ? `Failed During ${action} - with statusCode - ${statusCode}`
            : null);
      }

      serverObject.message =
        message ||
        (statusCode > STATUS_CODES.CREATED
          ? `Failed During ${action} - with statusCode - ${statusCode}`
          : null);
      serverObject.latestEventDtm = activityDtm;
      serverObject.statusCode = statusCode;
      // Update sub-step statuses without overwriting previous states

      if (serverKey === STEPPER_TITLES.GATEWAY) {
        if (action === ACTIONS.GATEWAY_STEP_ACTIONS.CREATE) {
          serverObject.actionStatus[ACTIONS.GATEWAY_STEP_ACTIONS.CREATE] =
            statusCode === STATUS_CODES.CREATED;
        }
        if (action === ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE) {
          serverObject.actionStatus[ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE] =
            statusCode === STATUS_CODES.SUCCESS ||
            statusCode === STATUS_CODES.CREATED;
        }
      } else if (serverKey === STEPPER_TITLES.VMS) {
        if (action === ACTIONS.VMS_STEP_ACTIONS.CREATE_SYNC) {
          serverObject.actionStatus[ACTIONS.VMS_STEP_ACTIONS.CREATE_SYNC] =
            statusCode === STATUS_CODES.SUCCESS ||
            statusCode === STATUS_CODES.CREATED;
        }
        if (action === ACTIONS.VMS_STEP_ACTIONS.UPDATE_SYNC) {
          serverObject.actionStatus[ACTIONS.VMS_STEP_ACTIONS.UPDATE_SYNC] =
            statusCode === STATUS_CODES.SUCCESS ||
            statusCode === STATUS_CODES.CREATED;
        }
        if (action === ACTIONS.VMS_STEP_ACTIONS.GET_SYNC) {
          serverObject.actionStatus[ACTIONS.VMS_STEP_ACTIONS.GET_SYNC] =
            statusCode === STATUS_CODES.SUCCESS ||
            statusCode === STATUS_CODES.CREATED;
        }
      } else if (serverKey === STEPPER_TITLES.CLIENTS) {
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC] =
            statusCode === STATUS_CODES.CREATED;
        }
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC] =
            statusCode === STATUS_CODES.SUCCESS;
        }
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC] =
            statusCode === STATUS_CODES.SUCCESS ||
            statusCode === STATUS_CODES.CREATED;
        }
      }
    }
  } catch (e) {
    console.error('An error occurred while processing the logs:', e);
    // The default object will be returned below.
  }
  // Final calculations for success and partialSuccess after all events are processed
  for (const server in stepperProgress) {
    if (
      Object.prototype.hasOwnProperty.call(stepperProgress, server) &&
      server !== 'eachStepSummary'
    ) {
      const subSteps = stepperProgress[server].actionStatus;
      const allStepsComplete = Object.values(subSteps).every(
        (status) => status === true,
      );
      const someStepsComplete = Object.values(subSteps).some(
        (status) => status === true,
      );

      stepperProgress[server].success = allStepsComplete;
      stepperProgress[server].partialSuccess =
        !allStepsComplete && someStepsComplete;
    }
  }

  const eachStepSummary: Record<string, AlertStepActionStatus> = {};
  for (const server in stepperProgress) {
    if (Object.prototype.hasOwnProperty.call(stepperProgress, server)) {
      eachStepSummary[server] = stepperProgress[server].actionStatus;
    }
  }
  // Return the object with eachStepSummary added as a property
  return {
    ...stepperProgress,
    eachStepSummary,
  } as CustomisedStepperResponse;
}

export const getLabelOfAlertStep = (
  stepIndexNumber: number,
  AllAlertSteps: string[],
  allowedStepsBasedOnProviderConfig: { stepTitle: string; label: string }[],
): string => {
  const stepTitle = AllAlertSteps[stepIndexNumber];
  const stepperLabel = allowedStepsBasedOnProviderConfig?.find(
    (step) => step.stepTitle === stepTitle,
  )?.label;
  return stepperLabel || '';
};

export const EXCLUDED_ALERT_TYPES = {
  PLANOGRAM_REJECTION: 'PLANOGRAM_REJECTION',
};
